import { useState, useEffect, useRef, useCallback } from "react";
import { AppState, AppStateStatus, Platform } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import {
  checkOpenHouseHealth,
  CheckOptions,
  NetworkHealthResult,
  NetworkHealthStatus,
  FailureReason,
} from "../utils/networkHealth";

interface NetworkHealthState extends NetworkHealthResult {
  lastChecked: number | null;
}

interface UseNetworkHealthConfig extends CheckOptions {
  pollIntervalMs?: number;
}

interface UseNetworkHealthReturn extends NetworkHealthState {
  isOnline: boolean;
  isOffline: boolean;
  isDegraded: boolean;
  hasChecked: boolean;
  refresh: () => Promise<NetworkHealthState>;
}

const DEFAULT_STATE: NetworkHealthState = {
  status: "online",
  latencyMs: null,
  attemptsUsed: 0,
  failureReason: undefined,
  lastChecked: null,
};

const DEFAULT_INTERVAL_MS = 30000;

const isNavigatorOffline = () => {
  if (Platform.OS !== "web") {
    return false;
  }

  if (typeof navigator === "undefined") {
    return false;
  }

  return navigator.onLine === false;
};

const useNetworkHealth = (
  config: UseNetworkHealthConfig = {}
): UseNetworkHealthReturn => {
  const {
    pollIntervalMs = DEFAULT_INTERVAL_MS,
    timeoutMs,
    attempts,
    degradeThresholdMs,
    retryDelayMs,
  } = config;

  const [state, setState] = useState<NetworkHealthState>(DEFAULT_STATE);
  const inFlightRef = useRef<Promise<NetworkHealthState> | null>(null);
  const lastStatusRef = useRef<NetworkHealthStatus>(DEFAULT_STATE.status);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const applyResult = useCallback((result: NetworkHealthResult): NetworkHealthState => {
    const now = Date.now();
    lastStatusRef.current = result.status;

    const next: NetworkHealthState = {
      ...result,
      lastChecked: now,
    };

    setState(next);
    return next;
  }, []);

  const setOfflineFromNavigator = useCallback(() => {
    const now = Date.now();
    const next: NetworkHealthState = {
      status: "offline",
      latencyMs: null,
      attemptsUsed: state.attemptsUsed,
      failureReason: "network" as FailureReason,
      lastChecked: now,
    };
    lastStatusRef.current = "offline";
    setState(next);
    return next;
  }, [state.attemptsUsed]);

  const refresh = useCallback(async () => {
    if (isNavigatorOffline()) {
      return setOfflineFromNavigator();
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const request = (async (): Promise<NetworkHealthState> => {
      const result = await checkOpenHouseHealth({
        timeoutMs,
        attempts,
        degradeThresholdMs,
        retryDelayMs,
      });
      return applyResult(result);
    })();

    inFlightRef.current = request;

    const resolved = await request;
    inFlightRef.current = null;
    return resolved;
  }, [applyResult, attempts, degradeThresholdMs, retryDelayMs, timeoutMs, setOfflineFromNavigator]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pollIntervalMs <= 0) {
      return;
    }

    const interval = setInterval(() => {
      refresh();
    }, pollIntervalMs);

    return () => clearInterval(interval);
  }, [pollIntervalMs, refresh]);

  useEffect(() => {
    const handler = (nextState: AppStateStatus) => {
      appStateRef.current = nextState;
      if (nextState === "active") {
        refresh();
      }
    };

    const subscription = AppState.addEventListener("change", handler);

    return () => {
      subscription.remove();
    };
  }, [refresh]);

  useFocusEffect(
    useCallback(() => {
      refresh();

      return () => {
        // no-op on blur
      };
    }, [refresh])
  );

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    const handleOnline = () => {
      refresh();
    };

    const handleOffline = () => {
      setOfflineFromNavigator();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refresh, setOfflineFromNavigator]);

  return {
    ...state,
    isOnline: state.status === "online",
    isOffline: state.status === "offline",
    isDegraded: state.status === "degraded",
    hasChecked: state.lastChecked !== null,
    refresh,
  };
};

export default useNetworkHealth;
